"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getContentfulEntriesByType } from './contentful.delivery';
import { usePixelatedConfig } from '../config/config.client';
import { PageSection } from '../structure/page-blocks';
import './contentful.alert.css';
const debug = false;
/**
 * Component to display alerts fetched from Contentful.
 *
 * @param {ContentfulAlertsType} props - Component props.
 * @returns {JSX.Element | null} The rendered component or null if no alerts are active.
 */
ContentfulAlerts.propTypes = {
    /** Contentful content type ID to query. */
    alertContentType: PropTypes.string,
};
export function ContentfulAlerts(props) {
    const config = usePixelatedConfig();
    const [alerts, setAlerts] = useState([]);
    const contentfulConfig = config?.integrations?.contentful;
    const apiProps = {
        proxyURL: contentfulConfig?.proxyURL,
        base_url: contentfulConfig?.base_url ?? '',
        space_id: contentfulConfig?.space_id ?? '',
        environment: contentfulConfig?.environment ?? '',
        delivery_access_token: contentfulConfig?.delivery_access_token ?? '',
    };
    const alertContentType = props.alertContentType ?? 'alert';
    const startDateField = 'startDate';
    const endDateField = 'endDate';
    const statusField = 'status';
    const activeStatusValue = 'Active';
    useEffect(() => {
        const fetchAlerts = async () => {
            if (!apiProps.base_url || !apiProps.space_id || !apiProps.environment || !apiProps.delivery_access_token) {
                return;
            }
            try {
                const response = await getContentfulEntriesByType({
                    apiProps,
                    contentType: alertContentType,
                });
                const items = Array.isArray(response?.items) ? response.items : [];
                const now = new Date();
                const activeAlerts = items
                    .filter((item) => {
                    const fields = item?.fields ?? {};
                    const statusValue = `${fields[statusField] ?? ''}`.trim();
                    if (statusValue.toLowerCase() !== activeStatusValue.toLowerCase()) {
                        return false;
                    }
                    const startValue = fields[startDateField];
                    const endValue = fields[endDateField];
                    const startDate = startValue ? new Date(startValue) : null;
                    const endDate = endValue ? new Date(endValue) : null;
                    if (startDate && now < startDate) {
                        return false;
                    }
                    if (endDate && now > endDate) {
                        return false;
                    }
                    return true;
                })
                    .sort((a, b) => {
                    const aEnd = a.fields?.[endDateField] ? new Date(a.fields[endDateField]).getTime() : Number.POSITIVE_INFINITY;
                    const bEnd = b.fields?.[endDateField] ? new Date(b.fields[endDateField]).getTime() : Number.POSITIVE_INFINITY;
                    return aEnd - bEnd;
                });
                setAlerts(activeAlerts);
            }
            catch (fetchError) {
                console.error('ContentfulAlerts fetch error:', fetchError);
            }
        };
        fetchAlerts();
    }, [
        apiProps.base_url,
        apiProps.space_id,
        apiProps.environment,
        apiProps.delivery_access_token,
        alertContentType,
        activeStatusValue,
        startDateField,
        endDateField,
        statusField,
    ]);
    if (alerts.length === 0) {
        return null;
    }
    return (_jsx(_Fragment, { children: alerts.map((alert, index) => (_jsx(ContentfulAlert, { title: alert.fields?.title ?? '', description: alert.fields?.description ?? '', id: alert.sys?.id ?? `alert-${index}`, index: index }, alert.sys?.id ?? index))) }));
}
/**
 * Component to display a single alert fetched from Contentful.
 *
 * @param {ContentfulAlertType} props - Component props.
 * @returns {JSX.Element} The rendered component.
 */
ContentfulAlert.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    id: PropTypes.string,
    index: PropTypes.number.isRequired,
};
export function ContentfulAlert(props) {
    const { title, description, id, index } = props;
    return (_jsx(PageSection, { columns: 1, maxWidth: "100%", id: `alerts-${index}-section`, children: _jsxs("div", { className: "contentful-alert contentful-alert-active", children: [title && _jsx("h3", { className: "contentful-alert-title", children: title }), description && _jsx("div", { className: "contentful-alert-description", children: description })] }) }));
}
